import { useState } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import {LayoutDashboard, FileText, Settings, BarChart3, Users, Menu, X, LogOut, ChevronRight, Recycle, Sprout} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'

const menuItems = [
  { path: '/dashboard',   label: 'Dashboard',   icon: LayoutDashboard },
  { path: '/solicitacoes', label: 'Solicitações', icon: FileText },
  { path: '/replantio',   label: 'Replantio',   icon: Sprout },
  { path: '/catadores',   label: 'Catadores',   icon: Recycle },
  { path: '/configuracoes', label: 'Configurações', icon: Settings },
  { path: '/relatorios',  label: 'Relatórios',  icon: BarChart3 },
  { path: '/usuarios',    label: 'Usuários',    icon: Users },
]

const BRAND = '#0f3b50'

export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    logout()
    toast.success('Sessão encerrada.')
    navigate('/login', { replace: true })
  }

  const isActive = (path: string) => location.pathname.startsWith(path)

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-100 z-30 flex flex-col transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-gray-100">
          <div
            className="w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{ backgroundColor: '#f0f7ed' }}
          >
            <img
              src="https://static.lumi.new/24/2440432f58c0c7516f7c4599415c87d6.webp"
              alt="GEOA"
              className="w-7 h-7 object-contain"
            />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-gray-900 leading-tight truncate">
              GEOA
            </p>
            <p className="text-xs text-gray-500 truncate">Gestão Online Ambiental</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          <ul className="space-y-0.5">
            {menuItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item.path)
              return (
                <li key={item.path}>
                  <button
                    onClick={() => { navigate(item.path); setSidebarOpen(false) }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition"
                    style={active
                      ? { backgroundColor: '#e8f1f5', color: BRAND }
                      : { color: '#4b5563' }
                    }
                    onMouseEnter={e => { if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f9fafb' }}
                    onMouseLeave={e => { if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent' }}
                  >
                    <Icon
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: active ? BRAND : '#9ca3af' }}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                    {active && <ChevronRight className="w-3.5 h-3.5" style={{ color: BRAND }} />}
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Footer do sidebar */}
        <div className="border-t border-gray-100 p-3">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <div
              className="w-7 h-7 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#e8f1f5' }}
            >
              <span className="text-xs font-bold" style={{ color: BRAND }}>
                {user?.userName?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-gray-900 truncate">
                {user?.userName || 'Usuário'}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {user?.userRole === 'ADMIN' ? 'Administrador' : 'Usuário'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-2xl transition"
          >
            <LogOut className="w-4 h-4" />
            Sair do sistema
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Topbar mobile */}
        <header className="lg:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#f0f7ed' }}>
              <img
                src="https://static.lumi.new/24/2440432f58c0c7516f7c4599415c87d6.webp"
                alt="GEOA"
                className="w-5 h-5 object-contain"
              />
            </div>
            <span className="text-sm font-semibold text-gray-900">GEOA – Gestão Online Ambiental</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
